import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ColumnFiltersState } from '@tanstack/react-table';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { type GridAction } from '@components/atoms/datatable/grid-action-controller';
import { PATHS } from '../../../router/paths';
import { PencilLine } from 'lucide-react';
import {
  ResellerSummaryRow,
  ResellerSummaryFilters
} from '@utils/types/back-office/usage-revenue';
import { formatCurrency } from '@utils/helpers/currency';
import { SupportedCurrency } from '@utils/types/organization/organization';

interface ResellerTableProps {
  data: ResellerSummaryRow[];
  isLoading: boolean;
  totalItems: number;
  pageSize?: number;
  offset?: number;
  onRowClick?: (row: ResellerSummaryRow) => void;
  onPaginationChange?: (offset: number) => void;
  onSortChange?: (sort: string | undefined, direction: 'asc' | 'desc') => void;
  onFilterChange?: (filters: ResellerSummaryFilters) => void;
  defaultSort?: { id: string; desc: boolean };
  currency?: SupportedCurrency;
}

const BE_SORT_FIELDS: Record<string, string> = {
  reseller_id: 'reseller_id',
  reseller_name: 'reseller_name',
  plan_name: 'plan_name',
  usage: 'stt_units'
};

export default function ResellerTable({
  data,
  isLoading,
  totalItems,
  pageSize = 25,
  onRowClick,
  onPaginationChange,
  onSortChange,
  onFilterChange,
  defaultSort,
  currency = 'EUR'
}: ResellerTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getColour } = useThemeContext();

  const handlePaginationChange = useCallback(
    (pagination: { pageIndex: number; pageSize: number }) => {
      const newOffset = pagination.pageIndex * pagination.pageSize;
      onPaginationChange?.(newOffset);
    },
    [onPaginationChange]
  );

  const handleSortChange = useCallback(
    (sortingState: Array<{ id: string; desc: boolean }>) => {
      if (sortingState.length === 0) {
        onSortChange?.(undefined, 'desc');
        return;
      }
      const mapped = BE_SORT_FIELDS[sortingState[0].id];
      if (!mapped) {
        onSortChange?.(undefined, 'desc');
        return;
      }
      onSortChange?.(mapped, sortingState[0].desc ? 'desc' : 'asc');
    },
    [onSortChange]
  );

  const handleFilterChange = useCallback(
    (filters: ColumnFiltersState) => {
      const find = (id: string) => filters.find(f => f.id === id)?.value;
      onFilterChange?.({
        resellerId:
          find('reseller_id') != null ? Number(find('reseller_id')) : undefined,
        search: find('reseller_name')
          ? String(find('reseller_name'))
          : undefined,
        planName: find('plan_name') ? String(find('plan_name')) : undefined,
        minUsage: find('usage') != null ? Number(find('usage')) : undefined
      });
    },
    [onFilterChange]
  );

  // Resellers do not share a currency either — SoftwareONE Philippines bills in
  // USD while the rest of the list is EUR — so format per row and keep the
  // prop as the fallback for responses that predate the field.
  const fmt = (value: number, rowCurrency?: SupportedCurrency) =>
    formatCurrency(value, rowCurrency ?? currency);

  const formatUsage = (stt: number, alignment: number, translation: number) => {
    const total = stt + alignment;
    if (translation > 0) {
      return `${total.toLocaleString()} units, ${translation.toLocaleString()} chars`;
    }
    return `${total.toLocaleString()} units`;
  };

  type Row = ResellerSummaryRow;

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        id: 'reseller_id',
        header: t('id'),
        field: 'reseller_id',
        accessorKey: 'reseller_id',
        maxWidth: 80,
        minWidth: 60,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'number',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'reseller_name',
        header: t('reseller-name'),
        field: 'reseller_name',
        accessorKey: 'reseller_name',
        minWidth: 200,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => (
          <span className="font-medium">{String(getValue() ?? '')}</span>
        )
      },
      {
        id: 'plan_name',
        header: t('plan'),
        field: 'plan_name',
        accessorKey: 'plan_name',
        minWidth: 150,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => (
          <span
            className="text-xs px-2 py-0.5 rounded-2xl"
            style={{
              backgroundColor: getColour('thirdLayer'),
              color: getColour('normalText')
            }}
          >
            {String(getValue() ?? '-')}
          </span>
        )
      },
      {
        id: 'usage',
        header: t('total-usage'),
        field: 'stt_units',
        accessorKey: 'stt_units',
        minWidth: 150,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'number',
        cell: ({ row }) =>
          formatUsage(
            row.original.stt_units,
            row.original.alignment_units,
            row.original.translation_characters
          )
      },
      {
        id: 'total_cost',
        header: t('total-cost'),
        field: 'total_cost',
        accessorKey: 'total_cost',
        minWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue, row }) =>
          fmt(Number(getValue() ?? 0), row.original.currency)
      }
    ],
    [t, getColour]
  );

  const openReseller = (row: Row) => {
    if (onRowClick) {
      onRowClick(row);
    } else {
      navigate(PATHS(String(row.reseller_id)).BO_RESELLER_USAGE_DETAIL);
    }
  };

  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'edit',
        label: t('view-details'),
        icon: <PencilLine className="h-3.5 w-3.5" />,
        intent: 'primary',
        run: openReseller
      }
    ],
    [t, navigate, onRowClick]
  );

  return (
    <DataGridV2<Row>
      data={data}
      columns={columns}
      actions={actions}
      enableRowSelection={false}
      enablePagination
      manualPagination
      enableSorting
      manualSorting
      enableFiltering
      manualFiltering
      showColumnFilters={false}
      filterMode="popover"
      totalItems={totalItems}
      pageSize={pageSize}
      pageSizeOptions={[25, 50, 75, 100]}
      fill
      dense
      grid={false}
      striped
      loading={isLoading}
      onRowClick={openReseller}
      onPaginationChange={handlePaginationChange}
      onSortingChange={handleSortChange}
      onFilteringChange={handleFilterChange}
      defaultSort={defaultSort ? [defaultSort] : []}
      getRowId={row => String(row.reseller_id)}
    />
  );
}
